import { differenceInYears, format } from "date-fns";
import { fr } from "date-fns/locale";
import { z } from "zod";

import type { ClaimWithRelations } from "../../api/use-claim";

export type UserClaim = ClaimWithRelations["usersClaims"][number];

export interface PassengerRow {
  /** userSegment.id — used as the passenger PATCH target. Null when the user segment was deleted. */
  id: number | null;
  /** userClaim.id — used as the family reassignment target. */
  userClaimId: string;
  passenger: {
    id: string;
    civility: string;
    contact: string | null;
    birthDate: Date | null;
  };
  booking: {
    isCodeshare: boolean;
    pnrId: string | null | undefined;
    bookingDate: string;
    flightIdentifier: string | undefined;
    airlineName: string | undefined;
  };
  familyId: string | null;
  familyName: string | null | undefined;
  /**
   * The address carried by this passenger's own userClaim — the only one an admin can remove
   * (TECH-1380). `contact` may instead come from the family acquisition or the user account.
   */
  ownEmail: string | null;
}

/**
 * Port of getCivilityTitle (Vue utils).
 * @param civilityCode The "ISO/IEC 5218" civility code.
 */
export function getCivilityTitle(civilityCode: number): string {
  if (civilityCode === 1) {
    return "Mr.";
  }
  if (civilityCode === 2) {
    return "Mde.";
  }
  return "";
}

export function isValidEmail(email: string | undefined): boolean {
  return Boolean(z.email().safeParse(email?.trim()).success);
}

const MAJORITY_AGE = 18;

/** Mineur à ce jour — sa cession est signée par un représentant légal, d'où l'affichage. */
export function isMinor(birthDate: Date | string, now = new Date()): boolean {
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return differenceInYears(now, date) < MAJORITY_AGE;
}

/** Port of the passengersByFamilies computed of PassengerTable.vue. */
export function groupPassengersByFamilies(claim: ClaimWithRelations): PassengerRow[][] {
  const familyAcquisitionEmailByFamilyId = new Map<string, string>();
  for (const family of claim.usersClaimsFamilies) {
    const acquisition = family.familyClaimAcquisition?.[0];
    if (acquisition?.toEmail) {
      familyAcquisitionEmailByFamilyId.set(family.id, acquisition.toEmail);
    }
  }

  const mapToTableData = (userClaim: UserClaim): PassengerRow => {
    const { user, userSegment, email, familyId } = userClaim;
    const civility = `${getCivilityTitle(user.civility)} ${user.firstName} ${user.lastName?.toUpperCase() ?? ""}`;
    const fcaEmail = familyId ? familyAcquisitionEmailByFamilyId.get(familyId) : undefined;
    const emailToShow = email || fcaEmail || user.email;
    const contact = emailToShow?.endsWith("upclaim.eu") ? null : (emailToShow ?? null);

    // The API types userSegment as required, but the relation is optional in DB.
    const segment = userSegment?.segment;
    const isCodeshare = Boolean(segment?.flight?.isCodeshare);

    return {
      id: userSegment?.id ?? null,
      userClaimId: userClaim.id,
      passenger: {
        id: user.id,
        civility,
        contact,
        birthDate: user.birthdate ?? null,
      },
      booking: {
        isCodeshare,
        pnrId: segment?.reservation?.gdsPnrId,
        bookingDate: segment?.reservation?.pnrCreatedAt
          ? format(new Date(segment.reservation.pnrCreatedAt), "d MMMM yyyy", { locale: fr })
          : "(inconnu)",
        flightIdentifier: segment?.flight.flightIdentifier,
        airlineName: segment?.flight.airline.name,
      },
      familyId: familyId ?? null,
      familyName: claim.usersClaimsFamilies.find((f) => f.id === familyId)?.familyName,
      ownEmail: contact && email ? email : null,
    };
  };

  const byFamilyId = new Map<string, PassengerRow[]>();
  for (const userClaim of claim.usersClaims) {
    const key = String(userClaim.familyId);
    const rows = byFamilyId.get(key) ?? [];
    rows.push(mapToTableData(userClaim));
    byFamilyId.set(key, rows);
  }

  return Array.from(byFamilyId.values());
}

/**
 * Moves one passenger into `targetFamilyId` and regroups. Empty families are dropped so
 * `updateClaimFamilies` deletes them server-side.
 */
export function movePassengerToFamily(
  families: PassengerRow[][],
  userClaimId: string,
  targetFamilyId: string,
): PassengerRow[][] {
  const targetFamilyName = families.flat().find((row) => row.familyId === targetFamilyId)?.familyName;

  const byFamilyId = new Map<string, PassengerRow[]>();
  for (const family of families) {
    for (const row of family) {
      const moved = row.userClaimId === userClaimId;
      const next = moved ? { ...row, familyId: targetFamilyId, familyName: targetFamilyName } : row;
      const key = String(next.familyId);
      const rows = byFamilyId.get(key) ?? [];
      rows.push(next);
      byFamilyId.set(key, rows);
    }
  }

  return Array.from(byFamilyId.values());
}

/** Payload of `updateClaimFamilies` — the full structure, families without id are skipped. */
export function toFamiliesPayload(
  families: PassengerRow[][],
): Array<{ familyId: string; usersClaimsIds: string[] }> {
  const payload: Array<{ familyId: string; usersClaimsIds: string[] }> = [];
  for (const family of families) {
    const familyId = family[0]?.familyId;
    if (!familyId) {
      continue;
    }
    payload.push({ familyId, usersClaimsIds: family.map((row) => row.userClaimId) });
  }
  return payload;
}
